'use strict';

/**
 * Sample-set customer tracker. Each publication is one sample set and one correlation.
 * Ready and Failed are the rollup of that sample set's graph nodes.
 * The project view aggregates those sample sets.
 */

import {
	CORE_PROGRESS_COPY,
	CORE_PROGRESS_STAGE_IDS,
	CORE_PROGRESS_STAGE_LABELS,
	CORE_PROGRESS_STAGE_ORDER,
	KITS_PROGRESS_STAGE_IDS,
} from './pipelineProgressCopy';
import { buildPipelineStatusGraphViewModel } from './pipelineStatusAdapter';
import { PIPELINE_PROGRESS_DEFAULT_DELAYED_AFTER_MS, buildPipelineProgressModel } from './pipelineProgressModel';
import { formatFlowDurationLabel, parseTimeMs } from './pipelineDuration';
import { activityDisplayNames } from './pipelineActivityHighLevelStep';

/** @param {string | null | undefined} correlationId */
function isEmptyPipelineCorrelation(correlationId) {
	const c = correlationId == null ? '' : String(correlationId).trim();
	return c === '' || c === '00000000-0000-0000-0000-000000000000';
}

export const CORE_SAMPLE_SET_FLOW_STATES = Object.freeze({
	received: 'received',
	preparing: 'preparing',
	publishing: 'publishing',
	ready: 'ready',
	failed: 'failed',
});

const KITS_INGEST_STAGES = new Set(['ManifestAvailable', 'ProjectAvailable']);

/** @param {string | undefined} iso */
function timeMs(iso) {
	return parseTimeMs(iso);
}

/** @param {unknown} v */
const trim = (v) => (v == null ? '' : String(v).trim());

/** @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts */
function latestSampleSetName(facts) {
	let bestName;
	let bestTime = Number.NEGATIVE_INFINITY;
	for (const f of facts) {
		const name = activityDisplayNames(f).sampleSetName;
		if (!name) continue;
		const t = timeMs(f.occurredAt) ?? Number.NEGATIVE_INFINITY;
		if (t >= bestTime) {
			bestTime = t;
			bestName = name;
		}
	}
	return bestName;
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @param {{ pipeline?: string, subjectId?: string, correlationId?: string }} publication
 * @param {string} [projectId]
 */
function factsForPublication(facts, publication, projectId) {
	const sampleSetId = trim(publication?.subjectId);
	const correlationId = trim(publication?.correlationId);
	const pipeline = trim(publication?.pipeline).toLowerCase();
	const project = trim(projectId);
	const rows = (Array.isArray(facts) ? facts : []).filter((f) => {
		if (!f) return false;
		if (trim(f.iterationValue) !== sampleSetId) return false;
		if (correlationId && !isEmptyPipelineCorrelation(correlationId)) {
			return trim(f.correlationId) === correlationId;
		}
		return true;
	});
	if (pipeline !== 'kits') return rows;
	const seen = new Set(rows.map((f) => f?.id).filter(Boolean));
	const ingest = (Array.isArray(facts) ? facts : []).filter((f) => {
		if (!f || seen.has(f.id)) return false;
		if (trim(f.pipeline).toLowerCase() !== 'kits') return false;
		if (!KITS_INGEST_STAGES.has(trim(f.stage))) return false;
		if (project && trim(f.subjectId) !== project) return false;
		return true;
	});
	return [...rows, ...ingest];
}

/**
 * Ready and Failed for one sample-set publication, from its graph nodes.
 * @param {{
 *   publication: { pipeline?: string, subjectId?: string, correlationId?: string, startedAt?: string, supersedesEarlier?: boolean },
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   definition: import('./pipelineStatusTypes').PipelineStatusGraphDefinition,
 *   projectId?: string,
 *   nowMs?: number,
 *   stageOrder?: readonly string[],
 *   stageLabels?: Record<string, string>,
 * }} args
 */
export function deriveSampleSetFlowFromGraph({
	publication,
	facts,
	definition,
	projectId,
	nowMs,
	stageOrder,
	stageLabels,
}) {
	const filtered = factsForPublication(facts, publication, projectId);
	const stages = buildPipelineStatusGraphViewModel(definition, filtered, [], projectId);
	const order = Array.isArray(stageOrder) && stageOrder.length ? stageOrder : CORE_PROGRESS_STAGE_ORDER;
	const labels = stageLabels ?? CORE_PROGRESS_STAGE_LABELS;
	const progress = buildPipelineProgressModel(stages, {
		supported: true,
		nowMs,
		startedAt: publication?.startedAt,
		stageOrder: order,
		stageLabels: labels,
	});
	const readyId = order[order.length - 1];
	const steps = progress.steps ?? [];
	const preparing = steps.find((s) => s.id === CORE_PROGRESS_STAGE_IDS.preparingResults);
	const publishing = steps.find((s) => s.id === CORE_PROGRESS_STAGE_IDS.publishing);
	const received = steps.find((s) => s.id === KITS_PROGRESS_STAGE_IDS.received);
	let state = CORE_SAMPLE_SET_FLOW_STATES.preparing;
	if (progress.state === 'ready') state = CORE_SAMPLE_SET_FLOW_STATES.ready;
	else if (progress.state === 'failed' || progress.state === 'unavailable') {
		state = progress.state === 'failed'
			? CORE_SAMPLE_SET_FLOW_STATES.failed
			: CORE_SAMPLE_SET_FLOW_STATES.preparing;
	} else if (
		received
		&& (received.state === 'complete' || received.state === 'active')
		&& steps.filter((s) => s.id !== received.id && s.id !== readyId).every((s) => s.state === 'pending')
	) {
		state = CORE_SAMPLE_SET_FLOW_STATES.received;
	} else if (publishing && publishing.state !== 'pending') {
		state = CORE_SAMPLE_SET_FLOW_STATES.publishing;
	} else if (preparing?.state === 'complete' && publishing?.state === 'pending') {
		state = CORE_SAMPLE_SET_FLOW_STATES.preparing;
	}

	const failedNode = stages.find((n) => n.status === 'failed');
	const terminalAt = stages
		.map((n) => n.occurredAt)
		.filter((iso) => iso != null && String(iso).trim() !== '')
		.sort()
		.at(-1);

	return {
		sampleSetId: trim(publication?.subjectId),
		correlationId: trim(publication?.correlationId),
		state,
		startedAt: publication?.startedAt,
		readyAt: state === CORE_SAMPLE_SET_FLOW_STATES.ready ? terminalAt : undefined,
		failedAt: state === CORE_SAMPLE_SET_FLOW_STATES.failed ? failedNode?.occurredAt : undefined,
		isRepublication: Boolean(publication?.supersedesEarlier),
		sampleSetName: latestSampleSetName(filtered),
		delayed: progress.state === 'delayed',
		steps,
	};
}

/**
 * @typedef {Object} CoreSampleSetProgress
 * @property {string} sampleSetId
 * @property {string} [correlationId] current flow correlation (for details fact scoping)
 * @property {string} name sample-set display name, falling back to the guid
 * @property {string} state one of CORE_SAMPLE_SET_FLOW_STATES
 * @property {boolean} delayed
 * @property {boolean} isRepublication previous results remain available while updating
 * @property {Array<{ id: string, label: string, state: 'pending'|'active'|'complete'|'failed'|'delayed' }>} steps
 * @property {string} message
 * @property {string | undefined} [durationLabel] overall sample-set flow duration for the main ticker
 */

/**
 * @typedef {Object} CorePipelineProgressModel
 * @property {boolean} supported
 * @property {'unavailable' | 'inProgress' | 'delayed' | 'failed' | 'ready'} state
 * @property {CoreSampleSetProgress[]} sampleSets
 * @property {{ ready: number, inProgress: number, failed: number, allReady: boolean } | null} summary
 * @property {string} message
 * @property {Array<{ id: string, label: string, state: 'pending'|'active'|'complete'|'failed' }>} [steps] idle ticker when supported with no current flow
 */

/** @param {readonly string[] | undefined} stageOrder @param {Record<string, string> | undefined} stageLabels */
function idleTickerSteps(stageOrder, stageLabels) {
	const order = Array.isArray(stageOrder) && stageOrder.length ? stageOrder : CORE_PROGRESS_STAGE_ORDER;
	const labels = stageLabels && typeof stageLabels === 'object' ? stageLabels : CORE_PROGRESS_STAGE_LABELS;
	return order.map((stageId) => ({
		id: stageId,
		label: labels[stageId] ?? CORE_PROGRESS_STAGE_LABELS[stageId] ?? stageId,
		state: 'pending',
	}));
}

/** @param {string} flowState @param {string | undefined} failureStage @param {boolean} delayed @param {readonly string[]} stageOrder @param {Record<string, string>} stageLabels */
function stepsForFlowState(flowState, failureStage, delayed, stageOrder, stageLabels) {
	const order = Array.isArray(stageOrder) && stageOrder.length ? stageOrder : CORE_PROGRESS_STAGE_ORDER;
	const labels = stageLabels && typeof stageLabels === 'object' ? stageLabels : CORE_PROGRESS_STAGE_LABELS;
	const stepState = (stageId) => {
		const isReceived = stageId === KITS_PROGRESS_STAGE_IDS.received;
		const isPreparing = stageId === CORE_PROGRESS_STAGE_IDS.preparingResults;
		const isPublishing = stageId === CORE_PROGRESS_STAGE_IDS.publishing;
		const isReady = stageId === CORE_PROGRESS_STAGE_IDS.ready;
		switch (flowState) {
			case CORE_SAMPLE_SET_FLOW_STATES.ready:
				return 'complete';
			case CORE_SAMPLE_SET_FLOW_STATES.failed:
				if (isReady) return 'pending';
				if (failureStage === CORE_PROGRESS_STAGE_IDS.preparingResults) {
					if (isPreparing) return 'failed';
					return isReceived ? 'complete' : 'pending';
				}
				if (isPublishing) return 'failed';
				return 'complete';
			case CORE_SAMPLE_SET_FLOW_STATES.publishing:
				if (isReceived || isPreparing) return 'complete';
				return isPublishing ? 'active' : 'pending';
			case CORE_SAMPLE_SET_FLOW_STATES.received:
				if (isReceived) return 'complete';
				return 'pending';
			default: // preparing
				if (isReceived) return 'complete';
				return isPreparing ? 'active' : 'pending';
		}
	};
	return order.map((stageId) => ({
		id: stageId,
		label: labels[stageId] ?? CORE_PROGRESS_STAGE_LABELS[stageId] ?? stageId,
		state: delayed && stepState(stageId) === 'active' ? 'delayed' : stepState(stageId),
	}));
}

/** @param {string} flowState @param {boolean} delayed @param {boolean} isRepublication @param {typeof CORE_PROGRESS_COPY} copy */
function messageForFlow(flowState, delayed, isRepublication, copy) {
	if (flowState === CORE_SAMPLE_SET_FLOW_STATES.failed) return copy.stateFailed;
	if (flowState === CORE_SAMPLE_SET_FLOW_STATES.ready) return copy.stateReady;
	if (isRepublication) return copy.updating;
	if (delayed) return copy.stateDelayed;
	if (flowState === CORE_SAMPLE_SET_FLOW_STATES.received) return copy.stateReceived ?? copy.statePreparing;
	return flowState === CORE_SAMPLE_SET_FLOW_STATES.publishing
		? copy.statePublishing
		: copy.statePreparing;
}

/**
 * Builds the Core customer tracker model.
 *
 * @param {Array<{
 *   sampleSetId: string, correlationId: string, state: string,
 *   startedAt?: string, readyAt?: string, failedAt?: string, failureStage?: string, isRepublication: boolean,
 *   sampleSetName?: string,
 *   steps?: Array<{ id: string, label: string, state: string }>,
 *   delayed?: boolean,
 * }>} flows graph-derived sample-set flows
 * @param {{
 *   supported?: boolean,
 *   nowMs?: number,
 *   delayedAfterMs?: number,
 *   copy?: typeof CORE_PROGRESS_COPY,
 *   stageOrder?: readonly string[],
 *   stageLabels?: Record<string, string>,
 * }} [options]
 * @returns {CorePipelineProgressModel}
 */
export function buildCorePipelineProgressModel(flows, options) {
	const supported = options?.supported !== false;
	const copy = options?.copy ?? CORE_PROGRESS_COPY;
	const nowMs = typeof options?.nowMs === 'number' ? options.nowMs : Date.now();
	const delayedAfterMs =
		typeof options?.delayedAfterMs === 'number' && options.delayedAfterMs > 0
			? options.delayedAfterMs
			: PIPELINE_PROGRESS_DEFAULT_DELAYED_AFTER_MS;
	const safeFlows = Array.isArray(flows) ? flows.filter((f) => f && trim(f.sampleSetId)) : [];

	if (!supported) {
		return {
			supported: false,
			state: 'unavailable',
			sampleSets: [],
			summary: null,
			message: copy.unavailable,
		};
	}

	if (!safeFlows.length) {
		return {
			supported: true,
			state: 'unavailable',
			sampleSets: [],
			summary: null,
			message: copy.emptyActivity,
			steps: idleTickerSteps(options?.stageOrder, options?.stageLabels),
		};
	}

	const sampleSets = safeFlows.map((flow) => {
		const inProgress =
			flow.state === CORE_SAMPLE_SET_FLOW_STATES.received
			|| flow.state === CORE_SAMPLE_SET_FLOW_STATES.preparing
			|| flow.state === CORE_SAMPLE_SET_FLOW_STATES.publishing;
		const startedMs = timeMs(flow.startedAt);
		const delayed = typeof flow.delayed === 'boolean'
			? flow.delayed
			: inProgress && startedMs != null && nowMs - startedMs > delayedAfterMs;
		const durationState =
			flow.state === CORE_SAMPLE_SET_FLOW_STATES.ready
				? 'ready'
				: flow.state === CORE_SAMPLE_SET_FLOW_STATES.failed
					? 'failed'
					: 'inProgress';
		return {
			sampleSetId: flow.sampleSetId,
			correlationId: flow.correlationId,
			name: trim(flow.sampleSetName) || flow.sampleSetId,
			state: flow.state,
			delayed,
			isRepublication: Boolean(flow.isRepublication),
			steps: Array.isArray(flow.steps) && flow.steps.length
				? flow.steps
				: stepsForFlowState(flow.state, flow.failureStage, delayed, options?.stageOrder, options?.stageLabels),
			message: messageForFlow(flow.state, delayed, Boolean(flow.isRepublication), copy),
			durationLabel: formatFlowDurationLabel({
				startedAt: flow.startedAt,
				endedAt:
					flow.state === CORE_SAMPLE_SET_FLOW_STATES.ready
						? flow.readyAt
						: flow.state === CORE_SAMPLE_SET_FLOW_STATES.failed
							? flow.failedAt
							: undefined,
				state: durationState,
				nowMs,
			}),
		};
	});

	const derivedSummary = {
		ready: sampleSets.filter((s) => s.state === CORE_SAMPLE_SET_FLOW_STATES.ready).length,
		failed: sampleSets.filter((s) => s.state === CORE_SAMPLE_SET_FLOW_STATES.failed).length,
		inProgress: sampleSets.filter(
			(s) => s.state !== CORE_SAMPLE_SET_FLOW_STATES.ready && s.state !== CORE_SAMPLE_SET_FLOW_STATES.failed,
		).length,
		allReady: sampleSets.length > 0 && sampleSets.every((s) => s.state === CORE_SAMPLE_SET_FLOW_STATES.ready),
	};

	let state = 'inProgress';
	let message;
	if (derivedSummary.failed > 0) {
		state = 'failed';
		message = copy.stateFailed;
	} else if (derivedSummary.allReady) {
		state = 'ready';
		message = copy.allReady;
	} else if (sampleSets.some((s) => s.delayed)) {
		state = 'delayed';
		message = copy.stateDelayed;
	} else if (sampleSets.some((s) => s.isRepublication && s.state !== CORE_SAMPLE_SET_FLOW_STATES.ready)) {
		message = copy.updating;
	} else {
		message = copy.statePublishing;
	}

	return { supported, state, sampleSets, summary: derivedSummary, message };
}
